'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/utils/supabase/client'
import { withDesktopClientFlag } from '@/lib/desktop/detect'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { Link04Icon, LoginMethodIcon } from '@hugeicons-pro/core-solid-rounded'
import { HugeiconsIcon } from '@hugeicons/react'
import { usePrefetch } from '@/hooks/usePrefetch'

interface AuthFormProps {
  title?: string
  subtitle: string
  showTerms?: boolean
  redirectPath?: string
  switchAuthLink?: {
    text: string
    linkText: string
    href: string
  }
  isSignup?: boolean
}

export function AuthForm({ 
  title,
  subtitle, 
  showTerms = true, 
  redirectPath = '/',
  switchAuthLink,
  isSignup = false
}: AuthFormProps) {
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { prefetch } = usePrefetch()

  // Prefetch likely navigation routes on mount
  useEffect(() => {
    const passwordPath = isSignup ? '/signup/password' : '/login/password'
    prefetch('/magic-link', { delay: 0 })
    prefetch(passwordPath, { delay: 0 })
    if (switchAuthLink?.href) {
      prefetch(switchAuthLink.href, { delay: 0 })
    }
  }, [prefetch, isSignup, switchAuthLink?.href])

  const handleOAuthSignIn = async (provider: 'google' | 'azure') => {
    setLoading(true)
    const supabase = createClient()

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: withDesktopClientFlag(`${window.location.origin}/auth/callback`),
        },
      })

      if (error) {
        toast.error(`Error signing in with ${provider === 'google' ? 'Google' : 'Microsoft'}`)
        setLoading(false)
      }
      // Don't set loading to false - we're redirecting
    } catch (error) {
      console.log('Error:', error)
      toast.error(`Error signing in with ${provider === 'google' ? 'Google' : 'Microsoft'}`)
      setLoading(false)
    }
  }

  return (
    <>
      <div
        className={`mx-auto mb-6 flex w-full max-w-md flex-col gap-1 px-4 text-center sm:px-6 md:px-0 ${loading ? 'animate-pulse opacity-40' : 'opacity-100'}`}
      >
        <span className="text-2xl font-black tracking-tight text-atrium-black">{title || 'Welcome back'}</span>
        <p className="mt-3 text-center text-sm font-medium text-atrium-black/60 sm:mt-4">{subtitle}</p>
        {switchAuthLink ? (
          <p className="mt-3 text-center text-sm font-medium text-atrium-black/60">
            {switchAuthLink.text}{' '}
            <Link
              href={switchAuthLink.href}
              prefetch={false}
              className="font-semibold text-atrium-black underline-offset-4 hover:underline"
            >
              {switchAuthLink.linkText}
            </Link>
          </p>
        ) : null}
      </div>

      <div className={`w-full max-w-md space-y-2 px-4 sm:px-6 md:px-0 ${loading ? 'animate-pulse opacity-40' : 'opacity-100'}`}>
        <div className="space-y-2 w-full">
          <Button
            type="button"
            onClick={() => {
              const passwordPath = isSignup ? '/signup/password' : '/login/password'
              router.push(passwordPath)
            }}
            variant="outline"
            className="w-full h-11 text-sm flex items-center justify-center gap-2"
            disabled={loading}
          >
            <HugeiconsIcon icon={LoginMethodIcon} className="size-5 -mr-1" />
            Continue with password
          </Button>
          <Button
            type="button"
            onClick={() => router.push('/magic-link')}
            variant="outline"
            className="w-full h-11 text-sm flex items-center justify-center gap-2"
            disabled={loading}
          >
            <HugeiconsIcon icon={Link04Icon} className="size-5 -mr-1" />
            Continue with link
          </Button>
          <Button
            type="button"
            onClick={() => handleOAuthSignIn('google')}
            variant="outline"
            className="w-full h-11 text-sm flex items-center justify-center gap-2"
            disabled={loading}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>
          {/* <Button
            type="button"
            onClick={() => handleOAuthSignIn('azure')}
            variant="outline"
            className="w-full h-11 text-sm flex items-center justify-center gap-2 bg-white text-atrium-black"
            disabled={loading}
          >
            <svg className="w-5 h-5" viewBox="0 0 23 23">
              <path fill="#F25022" d="M1 1h10v10H1z"/>
              <path fill="#7FBA00" d="M12 1h10v10H12z"/>
              <path fill="#00A4EF" d="M1 12h10v10H1z"/>
              <path fill="#FFB900" d="M12 12h10v10H12z"/>
            </svg>
            Continue with Microsoft
          </Button> */}
        </div>
      </div>

      <p className="mx-auto mt-8 max-w-xs px-4 text-center text-[13px] font-normal text-atrium-black/40 sm:px-0">
        By continuing, you agree to our <a href="/terms" target="_blank" className="underline hover:text-accent-600">Terms of service</a>.
      </p>
    </>
  )
} 